/**
 * Invoicing Page
 * BuilderBel Construction Project Management System
 *
 * Full-featured invoice list with stats, filters, action menus,
 * create/edit/payment drawers, and PDF download.
 */

import {
  Ban,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Eye,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Receipt,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { InvoiceDrawer, PaymentDrawer } from '../components/invoicing';
import { SearchableDropdown } from '../components/forms';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useCancelInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetAgingSummaryQuery,
  useGetInvoiceStatsQuery,
  useGetInvoicesQuery,
  useSendInvoiceMutation,
  useVoidInvoiceMutation,
} from '../services/invoicingApi';
import type { Invoice, InvoiceStatus, InvoiceType } from '../types/invoice';
import { INVOICE_TYPE_LABELS, PAYMENT_TERMS_LABELS } from '../types/invoice';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'void', label: 'Void' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'time_and_materials', label: 'Time & Materials' },
  { value: 'fixed_price', label: 'Fixed Price' },
  { value: 'service', label: 'Service' },
  { value: 'progress', label: 'Progress Billing' },
  { value: 'final', label: 'Final Invoice' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'invoiceDate', label: 'Invoice Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'total', label: 'Amount' },
  { value: 'invoiceNumber', label: 'Invoice Number' },
  { value: 'status', label: 'Status' },
];

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString?: string): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusVariant(
  status: InvoiceStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'partially_paid':
    case 'sent':
    case 'viewed':
      return 'warning';
    case 'overdue':
      return 'error';
    case 'void':
    case 'cancelled':
      return 'neutral';
    case 'draft':
    default:
      return 'neutral';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

const Invoicing: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // ---- Filters & Pagination ----
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [invoiceType, setInvoiceType] = useState<InvoiceType | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // ---- Drawer state ----
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);

  // ---- Active action menu ----
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // ---- Send invoice modal state ----
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);

  // ---- API queries ----
  const {
    data: invoicesResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetInvoicesQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        invoiceType: invoiceType || undefined,
        sortBy: sortBy as 'createdAt' | 'invoiceDate' | 'dueDate' | 'total' | 'invoiceNumber' | 'status',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetInvoiceStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const { data: agingResponse } = useGetAgingSummaryQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  // ---- Mutations ----
  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();
  const [voidInvoice] = useVoidInvoiceMutation();
  const [cancelInvoice] = useCancelInvoiceMutation();
  const [sendInvoice, { isLoading: isSending }] = useSendInvoiceMutation();

  // ---- Derived data ----
  const invoices = useMemo(() => invoicesResponse?.data?.invoices || [], [invoicesResponse]);
  const pagination = invoicesResponse?.data?.pagination;
  const stats = statsResponse?.data;
  const aging = agingResponse?.data;

  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total Invoiced',
        value: formatCurrency(stats.totalInvoiced),
        variant: 'brand' as const,
      },
      {
        title: 'Collected',
        value: formatCurrency(stats.collected),
        variant: 'success' as const,
      },
      {
        title: 'Outstanding',
        value: formatCurrency(stats.outstanding),
        variant: 'warning' as const,
      },
      {
        title: 'Overdue',
        value: formatCurrency(stats.overdueAmount),
        variant: 'danger' as const,
      },
    ];
  }, [stats]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCreate = useCallback(() => {
    setSelectedInvoice(null);
    setDrawerMode('create');
    setIsInvoiceDrawerOpen(true);
  }, []);

  const handleView = useCallback((invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDrawerMode('view');
    setIsInvoiceDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDrawerMode('edit');
    setIsInvoiceDrawerOpen(true);
  }, []);

  const handleCloseInvoiceDrawer = useCallback(() => {
    setIsInvoiceDrawerOpen(false);
    setSelectedInvoice(null);
  }, []);

  const handleOpenPaymentDrawer = useCallback((invoice: Invoice) => {
    setPaymentInvoice(invoice);
    setIsPaymentDrawerOpen(true);
  }, []);

  const handleClosePaymentDrawer = useCallback(() => {
    setIsPaymentDrawerOpen(false);
    setPaymentInvoice(null);
  }, []);

  const handleDelete = useCallback(
    async (invoice: Invoice) => {
      if (!projectId) return;
      if (!window.confirm(`Delete Invoice ${invoice.invoiceNumber}? This cannot be undone.`)) return;

      try {
        await deleteInvoice({ projectId, invoiceId: invoice._id }).unwrap();
        toast.success('Invoice deleted');
      } catch {
        toast.error('Failed to delete invoice');
      }
    },
    [projectId, deleteInvoice, toast]
  );

  const handleVoid = useCallback(
    async (invoice: Invoice) => {
      if (!projectId) return;
      const reason = window.prompt('Reason for voiding this invoice (optional):');
      if (reason === null) return; // Cancelled

      try {
        await voidInvoice({ projectId, invoiceId: invoice._id, data: { reason } }).unwrap();
        toast.success('Invoice voided');
        refetch();
      } catch {
        toast.error('Failed to void invoice');
      }
    },
    [projectId, voidInvoice, toast, refetch]
  );

  const handleCancel = useCallback(
    async (invoice: Invoice) => {
      if (!projectId) return;
      const reason = window.prompt('Reason for cancelling this invoice (optional):');
      if (reason === null) return;

      try {
        await cancelInvoice({ projectId, invoiceId: invoice._id, data: { reason } }).unwrap();
        toast.success('Invoice cancelled');
        refetch();
      } catch {
        toast.error('Failed to cancel invoice');
      }
    },
    [projectId, cancelInvoice, toast, refetch]
  );

  const handleSendInvoice = useCallback(
    async (invoice: Invoice) => {
      if (!projectId) return;
      const email = invoice.clientEmail || window.prompt('Enter recipient email address:');
      if (!email) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid email address');
        return;
      }

      setSendingInvoiceId(invoice._id);
      try {
        await sendInvoice({
          projectId,
          invoiceId: invoice._id,
          data: { recipientEmail: email, includePdf: true },
        }).unwrap();
        toast.success(`Invoice sent to ${email}`);
        refetch();
      } catch {
        toast.error('Failed to send invoice');
      } finally {
        setSendingInvoiceId(null);
      }
    },
    [projectId, sendInvoice, toast, refetch]
  );

  const handleDownloadPdf = useCallback(
    async (invoice: Invoice) => {
      if (!projectId) return;
      const token = localStorage.getItem('auth_token');
      const url = `/api/projects/${projectId}/invoices/${invoice._id}/pdf`;
      try {
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('PDF download failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `invoice-${invoice.invoiceNumber}.pdf`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={val => {
          setStatus(val as InvoiceStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Type"
        value={invoiceType}
        onChange={val => {
          setInvoiceType(val as InvoiceType | '');
          setPage(1);
        }}
        options={TYPE_OPTIONS}
        searchable={false}
      />
    </div>
  );

  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const { page: currentPage, pages: totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}–{endItem} of {total} invoices
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderRowActions = (invoice: Invoice) => {
    const isMenuOpen = activeMenuId === invoice._id;
    const isBeingSent = sendingInvoiceId === invoice._id;
    const isSentOrLater = ['sent', 'viewed', 'partially_paid', 'paid', 'overdue'].includes(
      invoice.status
    );
    const canEdit = ['draft'].includes(invoice.status);
    const canSend = !['paid', 'void', 'cancelled'].includes(invoice.status);
    const canPayment = ['sent', 'viewed', 'partially_paid', 'overdue'].includes(invoice.status);
    const canVoid = ['draft', 'sent', 'viewed', 'overdue', 'partially_paid'].includes(invoice.status);
    const canCancel = !['paid', 'void', 'cancelled'].includes(invoice.status);
    const canDelete = ['draft', 'cancelled'].includes(invoice.status);

    const menuItems: (ActionMenuItem | 'divider')[] = [
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleView(invoice),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEdit(invoice),
        hidden: !canEdit,
      },
      'divider',
      {
        label: isBeingSent ? 'Sending...' : 'Send Invoice',
        icon: <Send className="h-3.5 w-3.5" />,
        onClick: () => handleSendInvoice(invoice),
        hidden: !canSend,
        disabled: isSending,
      },
      {
        label: 'Record Payment',
        icon: <CreditCard className="h-3.5 w-3.5" />,
        onClick: () => handleOpenPaymentDrawer(invoice),
        variant: 'success',
        hidden: !canPayment,
      },
      {
        label: 'Download PDF',
        icon: <Download className="h-3.5 w-3.5" />,
        onClick: () => handleDownloadPdf(invoice),
        hidden: invoice.status === 'draft',
      },
      'divider',
      {
        label: 'Void Invoice',
        icon: <XCircle className="h-3.5 w-3.5" />,
        onClick: () => handleVoid(invoice),
        variant: 'warning',
        hidden: !canVoid,
      },
      {
        label: 'Cancel Invoice',
        icon: <Ban className="h-3.5 w-3.5" />,
        onClick: () => handleCancel(invoice),
        variant: 'warning',
        hidden: !canCancel,
      },
      'divider',
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDelete(invoice),
        variant: 'danger',
        disabled: isDeleting,
        hidden: !canDelete,
      },
    ];

    return (
      <ActionMenu
        id={invoice._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? invoice._id : null)}
      />
    );
  };

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================

  if (isLoading && invoices.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view invoices."
        />
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <ModuleLayout
      title="Invoicing"
      description="Manage T&M, fixed-price, and service invoices"
      icon={<Receipt className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Invoice',
        onClick: handleCreate,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={val => {
        setSearch(val);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {/* AR Aging Banner */}
      {aging && aging.total.amount > 0 && (
        <div className="mb-4 grid grid-cols-5 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Current</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(aging.current.amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">1–30 Days</p>
            <p className="text-sm font-semibold text-orange-600">
              {formatCurrency(aging.days1to30.amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">31–60 Days</p>
            <p className="text-sm font-semibold text-orange-700">
              {formatCurrency(aging.days31to60.amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">61–90 Days</p>
            <p className="text-sm font-semibold text-red-600">
              {formatCurrency(aging.days61to90.amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">90+ Days</p>
            <p className="text-sm font-semibold text-red-700">
              {formatCurrency(aging.over90.amount)}
            </p>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {invoices.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No invoices found"
          description={
            search || status || invoiceType
              ? "Try adjusting your filters to find what you're looking for."
              : 'Create your first invoice to start billing clients.'
          }
          action={
            !search && !status && !invoiceType
              ? { label: 'Create Invoice', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map(invoice => (
                <tr
                  key={invoice._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleView(invoice)}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </span>
                      {invoice.title && (
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {invoice.title}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-900">{invoice.clientName}</span>
                      {invoice.clientEmail && (
                        <span className="text-xs text-gray-500 truncate max-w-[180px]">
                          {invoice.clientEmail}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">
                      {INVOICE_TYPE_LABELS[invoice.invoiceType] || invoice.invoiceType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                      {invoice.balanceDue > 0 && invoice.status !== 'draft' && (
                        <span className="text-xs text-orange-600">
                          Due: {formatCurrency(invoice.balanceDue, invoice.currency)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={invoice.status.replace(/_/g, ' ')}
                      variant={getStatusVariant(invoice.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatDate(invoice.invoiceDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        invoice.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {formatDate(invoice.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(invoice)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawers */}
      <InvoiceDrawer
        isOpen={isInvoiceDrawerOpen}
        onClose={handleCloseInvoiceDrawer}
        invoice={selectedInvoice}
        mode={drawerMode}
        onSuccess={refetch}
      />

      <PaymentDrawer
        isOpen={isPaymentDrawerOpen}
        onClose={handleClosePaymentDrawer}
        invoice={paymentInvoice}
        onSuccess={refetch}
      />
    </ModuleLayout>
  );
};

export default Invoicing;
